import { createContext, MiddlewareFunction, replace } from 'react-router';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { AppState, AppStore } from '~/stores/app';
import { appStoreContext } from '~/utils/routing';
import { claimTypeParamSchema } from '~/utils/schemas';

const paramsSchema = z.object({
  store: z.string(),
  idFromPlatform: z.string(),
  claimType: claimTypeParamSchema,
});

const startNewClaim = (
  params: z.infer<typeof paramsSchema>,
  appStore: AppStore,
) => {
  const { claimType, idFromPlatform: orderIdFromPlatform } = params;

  appStore.dispatch(
    appStore.actions.crewClaim.startClaim({
      claimType,
      orderIdFromPlatform,
    }),
  );

  const { crewClaim } = appStore.getState();

  if (crewClaim === null) {
    throw new Error('Crew claim state is not initialized');
  }

  return crewClaim;
};

const isClaimInProgress = (
  crewClaim: NonNullable<AppState['crewClaim']>,
  params: z.infer<typeof paramsSchema>,
) =>
  crewClaim.status !== 'completed' &&
  crewClaim.claimType === params.claimType &&
  crewClaim.orderIdFromPlatform === params.idFromPlatform;

export type CrewClaim = NonNullable<AppState['crewClaim']>;
export const crewClaimContext = createContext<CrewClaim>();

/**
 * Initializes the claim workflow at the summary entry point.
 * Reuses the existing claim if it's in progress, otherwise starts a fresh one.
 */
export const startClaimMiddleware: MiddlewareFunction = ({
  context,
  params: rawParams,
}) => {
  const appStore = context.get(appStoreContext);
  const params = paramsSchema.parse(rawParams);
  const { crewClaim } = appStore.getState();

  const validClaim =
    crewClaim && isClaimInProgress(crewClaim, params)
      ? crewClaim
      : startNewClaim(params, appStore);

  context.set(crewClaimContext, validClaim);
};

/**
 * Validates that an in-progress claim exists and sets it in context.
 * Clears stale state and redirects to the order hub on any invalid combination.
 */
export const crewClaimMiddleware: MiddlewareFunction = ({
  context,
  params: rawParams,
}) => {
  const appStore = context.get(appStoreContext);
  const params = paramsSchema.parse(rawParams);
  const { crewClaim } = appStore.getState();

  match({ crewClaim, params })
    .with(
      { crewClaim: P.nonNullable },
      ({ crewClaim: c, params: p }) => isClaimInProgress(c, p),
      ({ crewClaim: c }) => context.set(crewClaimContext, c),
    )
    .otherwise(() => {
      // clear out invalid claim from state to prevent future invalid matches
      appStore.dispatch(appStore.actions.crewClaim.clearClaim());
      throw replace(`/${params.store}/order/${params.idFromPlatform}`);
    });
};

/**
 * Guards downstream routes that require at least one claim line item.
 * Runs after crewClaimMiddleware has already validated and set crewClaimContext.
 */
export const requireClaimLineItemsMiddleware: MiddlewareFunction = ({
  context,
  params: rawParams,
}) => {
  const { store, idFromPlatform } = paramsSchema.parse(rawParams);
  const crewClaim = context.get(crewClaimContext);

  if (crewClaim.claimLineItems.length === 0) {
    throw replace(`/${store}/order/${idFromPlatform}`);
  }
};
